/**
 * Mathematical utility functions
 *
 * Common game math operations:
 * - Distance and angle calculations
 * - Vector normalization
 * - Linear interpolation
 * - Value clamping
 * - Boundary checking
 */

/**
 * Calculate Euclidean distance between two objects with x,y coordinates
 * @param {Object} obj1 - First object with x, y properties
 * @param {Object} obj2 - Second object with x, y properties
 * @returns {number} Distance between the two objects
 */
export function getDistance(obj1, obj2) {
  const dx = obj2.x - obj1.x;
  const dy = obj2.y - obj1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Normalize a 2D vector to unit length (magnitude = 1)
 * Used for direction vectors in movement and aiming.
 *
 * @param {number} dx - X component of vector
 * @param {number} dy - Y component of vector
 * @returns {Object} Normalized vector {x, y} or {0, 0} if zero-length
 */
export function normalizeVector(dx, dy) {
  const distance = Math.sqrt(dx * dx + dy * dy);
  if (distance === 0) return { x: 0, y: 0 };
  return { x: dx / distance, y: dy / distance };
}

/**
 * Calculate angle in radians from one point to another
 * Result is in range [-π, π] where 0 = right, π/2 = down
 *
 * @param {number} fromX - Starting X coordinate
 * @param {number} fromY - Starting Y coordinate
 * @param {number} toX - Target X coordinate
 * @param {number} toY - Target Y coordinate
 * @returns {number} Angle in radians
 */
export function calculateAngle(fromX, fromY, toX, toY) {
  return Math.atan2(toY - fromY, toX - fromX);
}

/**
 * Clamp a value between minimum and maximum bounds
 * @param {number} value - Value to clamp
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {number} Clamped value in range [min, max]
 */
export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Linear interpolation between two values
 * @param {number} start - Start value (factor = 0)
 * @param {number} end - End value (factor = 1)
 * @param {number} factor - Interpolation factor (0 to 1)
 * @returns {number} Interpolated value
 */
export function lerp(start, end, factor) {
  return start + (end - start) * factor;
}

/**
 * Generate random number in range [min, max)
 * @param {number} min - Minimum value (inclusive)
 * @param {number} max - Maximum value (exclusive)
 * @returns {number} Random number in range
 */
export function randomRange(min, max) {
  return min + Math.random() * (max - min);
}

/**
 * Check if coordinates are within bounds with optional margin
 * @param {number} x - X coordinate to check
 * @param {number} y - Y coordinate to check
 * @param {number} width - Boundary width
 * @param {number} height - Boundary height
 * @param {number} margin - Optional margin from edges (default 0)
 * @returns {boolean} True if point is within bounds
 */
export function isInBounds(x, y, width, height, margin = 0) {
  return x >= margin && x <= width - margin &&
         y >= margin && y <= height - margin;
}